// src/components/reuseable/InlineEdit.jsx
// Shared inline-edit widgets used by the pipeline detail panels so they edit
// identically. Each widget renders an input/select/control when the viewer can
// manage the lead (saving immediately via onSave) and read-only text otherwise.
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

export const fmtPrice = (n) =>
  n ? `$${Number(n).toLocaleString("en-US")}` : "";

// Coerce a possibly-object field into a safe display string. Some migrated leads
// stored an OBJECT where a string was expected; rendering it raw throws React
// error #31 and blanks the page.
export const text = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object")
    return v.name || [v.firstname, v.lastname].filter(Boolean).join(" ") || "";
  return "";
};

// Dates arrive as epoch-ms or YYYY-MM-DD. Parse YYYY-MM-DD as LOCAL midnight.
export const fmtDate = (v) => {
  if (v == null || v === "") return "";
  const s = String(v);
  let d;
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  else if (/^\d+$/.test(s)) d = new Date(Number(s));
  else d = new Date(s);
  if (!d || isNaN(d.getTime())) return text(v);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Parse a typed money/number string to a stored number (or "" to clear).
export const toNum = (v) => {
  const n = Number(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : "";
};

export const arrayToCsv = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
export const csvToArray = (s) =>
  String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

const ROW =
  "flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2";
const LBL = "text-slate-500 flex items-center gap-1.5 shrink-0";
const CTRL =
  "text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B]";
const Saved = ({ on }) =>
  on ? <span className="text-[10px] text-emerald-600">Saved ✓</span> : null;

export const Row = ({ k, v }) => {
  const val = text(v);
  return val ? (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium text-right">{val}</span>
    </div>
  ) : null;
};

export const EditableDateRow = ({ k, value, canEdit, onSave }) => {
  const [val, setVal] = useState(value || "");
  const [flash, setFlash] = useState(false);
  useEffect(() => setVal(value || ""), [value]);
  if (!canEdit) {
    const disp = fmtDate(value);
    return disp ? <Row k={k} v={disp} /> : null;
  }
  const handle = async (e) => {
    const next = e.target.value;
    const prev = val;
    setVal(next);
    const res = await onSave(next);
    if (res && res.success === false) {
      setVal(prev);
      toast.error("Couldn't save the date — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };
  return (
    <div className={ROW}>
      <span className={LBL}>{k}<Saved on={flash} /></span>
      <input type="date" value={val} onChange={handle} className={CTRL} />
    </div>
  );
};

export const EditableTextRow = ({ k, value, canEdit, onSave, type = "text", display, placeholder }) => {
  const [val, setVal] = useState(value == null ? "" : value);
  const [flash, setFlash] = useState(false);
  useEffect(() => setVal(value == null ? "" : value), [value]);
  if (!canEdit) {
    const shown = display ? display(value) : text(value);
    return shown ? <Row k={k} v={shown} /> : null;
  }
  const commit = async () => {
    const same =
      type === "number"
        ? toNum(val) === toNum(value)
        : String(val) === String(value == null ? "" : value);
    if (same) return;
    const res = await onSave(val);
    if (res && res.success === false) {
      setVal(value == null ? "" : value);
      toast.error("Couldn't save — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };
  return (
    <div className={ROW}>
      <span className={LBL}>{k}<Saved on={flash} /></span>
      <input
        type="text"
        inputMode={type === "number" ? "decimal" : undefined}
        value={val}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className={`${CTRL} text-right w-[58%]`}
      />
    </div>
  );
};

export const EditableSelectRow = ({ k, value, canEdit, onSave, options, display, placeholder = "—" }) => {
  const [flash, setFlash] = useState(false);
  if (!canEdit) {
    const shown = display ? display(value) : text(value);
    return shown ? <Row k={k} v={shown} /> : null;
  }
  const handle = async (e) => {
    const res = await onSave(e.target.value);
    if (res && res.success === false) toast.error("Couldn't save — try again");
    else { setFlash(true); setTimeout(() => setFlash(false), 1200); }
  };
  return (
    <div className={ROW}>
      <span className={LBL}>{k}<Saved on={flash} /></span>
      <select value={value == null ? "" : value} onChange={handle} className={`${CTRL} max-w-[58%]`}>
        <option value="">{placeholder}</option>
        {value != null && value !== "" &&
          !options.some((o) => String(o.value) === String(value)) && (
            <option value={value}>{(display ? display(value) : text(value)) || String(value)}</option>
          )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

// Multi-select toggle chips (e.g. Property Type). `value` is string[].
export const EditableChipsRow = ({ k, value, canEdit, onSave, options }) => {
  const arr = Array.isArray(value) ? value : [];
  if (!canEdit) return arr.length ? <Row k={k} v={arr.join(", ")} /> : null;
  const toggle = (opt) =>
    onSave(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
  return (
    <div className="py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{k}</span>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {options.map((opt) => {
          const on = arr.includes(opt);
          return (
            <button type="button" key={opt} onClick={() => toggle(opt)}
              className={`text-xs font-medium px-2.5 py-1 rounded-full border ${on ? "bg-violet-100 border-violet-300 text-violet-700" : "bg-white border-slate-300 text-slate-600"}`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Comma-separated list editor (Areas / Must-Haves / Tags). Stores string[],
// edits as raw text so commas + trailing spaces survive while typing.
export const EditableCsvRow = ({ k, value, canEdit, onSave, placeholder }) => {
  const [csv, setCsv] = useState(arrayToCsv(value));
  const [focused, setFocused] = useState(false);
  const [flash, setFlash] = useState(false);
  useEffect(() => { if (!focused) setCsv(arrayToCsv(value)); }, [value, focused]);
  if (!canEdit) {
    const arr = Array.isArray(value) ? value : [];
    return arr.length ? <Row k={k} v={arr.join(", ")} /> : null;
  }
  const commit = async () => {
    setFocused(false);
    const next = csvToArray(csv);
    if (arrayToCsv(value) === next.join(", ")) return;
    const res = await onSave(next);
    if (res && res.success === false) toast.error("Couldn't save — try again");
    else { setFlash(true); setTimeout(() => setFlash(false), 1200); }
  };
  return (
    <div className={ROW}>
      <span className={LBL}>{k}<Saved on={flash} /></span>
      <input type="text" value={csv} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={commit}
        onChange={(e) => setCsv(e.target.value)}
        className={`${CTRL} w-[58%]`} />
    </div>
  );
};
